import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, ArrowLeft, Loader2, Search, Building2, MapPin, 
  Briefcase, Star, Mail, Phone, CheckCircle, XCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobCandidatesPage() {
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [minScore, setMinScore] = useState(50);
  const [limit, setLimit] = useState(50);

  const loadJob = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJob(data);
      }
    } catch (e) {
      console.error('Error loading job:', e);
    }
  };

  const findCandidates = async () => {
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/jobs/${jobId}/find-candidates?min_score=${minScore}&limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setCandidates(data.candidates || []);
        toast.success(`Found ${data.total_candidates_found} matching candidates`);
      } else {
        toast.error('Failed to find candidates');
      }
    } catch (e) {
      toast.error('Error searching for candidates');
    }
    setSearching(false);
  };

  useEffect(() => {
    loadJob();
    findCandidates();
  }, [jobId]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Back button and Job Info */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/ats/jobs')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </Button>
          
          {job && (
            <Card className="mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{job.job_title}</CardTitle>
                    <p className="text-slate-500 mt-1 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {job.company_name}
                      {job.location && (
                        <>
                          <span className="text-slate-300">|</span>
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </>
                      )}
                    </p>
                  </div>
                  <Badge variant="outline">{job.status}</Badge>
                </div>
              </CardHeader>
            </Card>
          )}
        </div>

        {/* Search Controls */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-end gap-4">
              <div>
                <Label className="text-sm">Minimum Match Score</Label>
                <Input
                  type="number"
                  value={minScore}
                  onChange={(e) => setMinScore(parseInt(e.target.value) || 0)}
                  min={0}
                  max={100}
                  className="w-24"
                />
              </div>
              <div>
                <Label className="text-sm">Max Results</Label>
                <Input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
                  min={1}
                  max={200}
                  className="w-24"
                />
              </div>
              <Button onClick={findCandidates} disabled={searching}>
                {searching ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {searching ? 'Searching...' : 'Find Candidates'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Matching Candidates ({candidates.length})
          </h2>
        </div>

        {loading && !candidates.length ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : candidates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No candidates found matching the criteria.</p>
              <p className="text-sm mt-1">Try lowering the minimum score threshold.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {candidates.map((candidate, index) => (
              <Card key={candidate.candidate_id} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${getScoreColor(candidate.match_score)}`}>
                        {candidate.match_score}
                      </div>
                      <div>
                        <Link 
                          to={`/ats/candidates/${candidate.candidate_id}`}
                          className="font-medium text-slate-800 hover:text-blue-600"
                        >
                          {candidate.candidate_name}
                        </Link>
                        <div className="text-sm text-slate-500 mt-1 flex items-center gap-3">
                          {candidate.current_designation && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              {candidate.current_designation}
                            </span>
                          )}
                          {candidate.current_company && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {candidate.current_company}
                            </span>
                          )}
                          {candidate.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {candidate.location}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          {candidate.candidate_email && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Mail className="w-3 h-3" />
                              {candidate.candidate_email}
                            </span>
                          )}
                          {candidate.candidate_phone && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Phone className="w-3 h-3" />
                              {candidate.candidate_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-slate-600 mb-2">
                        <span className="font-medium">Exp:</span> {candidate.total_experience || 'N/A'} yrs
                      </div>
                      <div className="flex flex-wrap gap-1 max-w-xs justify-end">
                        {candidate.matching_skills?.slice(0, 5).map((skill, i) => (
                          <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {skill}
                          </Badge>
                        ))}
                        {candidate.missing_skills?.slice(0, 3).map((skill, i) => (
                          <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                            <XCircle className="w-3 h-3 mr-1" />
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
